/**
 * 個股體質整合面板：把「風險 / 財報 / 基本面 / 籌碼」整併成單一卡片。
 *
 * 版面策略（符合 PanelCard 鐵則「不要整張收合、用卡內摘要壓縮次級資訊」）：
 * - 標題下常駐「一行健康度摘要 chips」（四面向各一個關鍵數字），一眼看完不必逐一展開。
 * - 明細（4 個重 tab）預設收起、點 chip 或「展開明細」才載入，平常只佔一行、省版面。
 * - 摘要數字來自彙總 endpoint /api/health-summary（後端 allSettled 並行四來源）。
 * - 台股才有「籌碼」分頁 / chip；美股只有風險 / 財報 / 基本面。
 */
import { useState } from 'react';
import { Activity, CalendarClock, Building2, Layers, ChevronDown, ChevronRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import type { HealthSummary } from '@invest-copilot/shared';
import { fetchJSON } from '../lib/api-client';
import { RiskDashboardPanel } from './RiskDashboardPanel';
import { EarningsCockpit } from './EarningsCockpit';
import { FundamentalsCard } from './FundamentalsCard';
import { InstitutionalCard } from './InstitutionalCard';
import { MarginCard } from './MarginCard';

type HealthTab = 'risk' | 'earnings' | 'fundamentals' | 'chips';
type ChipTone = 'up' | 'down' | 'flat' | 'accent';

interface TabMeta {
  id: HealthTab;
  label: string;
  Icon: typeof Activity;
}

const RISK_LEVEL_LABEL: Record<'low' | 'mid' | 'high', string> = { low: '低', mid: '中', high: '高' };

/** 前端即時算「距某日期還有幾天」，以使用者本地今天為準（不信任後端可能 stale 的相對天數）。 */
function daysUntil(dateStr?: string): number | undefined {
  if (!dateStr) return undefined;
  const target = new Date(`${dateStr}T00:00:00`);
  if (!Number.isFinite(target.getTime())) return undefined;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

/** 從進場機會「研究台看依據」帶入的 AI 風險判讀（researchPlan.decision 子集），整合進個股體質卡。 */
export interface AiRisk {
  failureConditions?: string[];
  riskWarnings?: string[];
}

export function StockHealthPanel({ symbol, isTw, aiRisk }: { symbol: string; isTw: boolean; aiRisk?: AiRisk | null }) {
  const [tab, setTab] = useState<HealthTab>('risk');
  // 預設展開：個股體質在 K 線旁右欄，展開風險明細填滿高度與左欄對齊（chips 摘要仍常駐在上）
  const [expanded, setExpanded] = useState(true);

  const summaryQ = useQuery({
    queryKey: ['health-summary', symbol],
    queryFn: () => fetchJSON<HealthSummary>(`/api/health-summary/${encodeURIComponent(symbol)}`),
    enabled: !!symbol,
    staleTime: 15 * 60_000,
    retry: 1,
  });
  const s = summaryQ.data;

  const tabs: TabMeta[] = [
    { id: 'risk', label: '風險', Icon: Activity },
    { id: 'earnings', label: '財報', Icon: CalendarClock },
    { id: 'fundamentals', label: '基本面', Icon: Building2 },
    ...(isTw ? [{ id: 'chips' as const, label: '籌碼', Icon: Layers }] : []),
  ];

  // 切到美股時若停在「籌碼」分頁，回退到風險
  const activeTab: HealthTab = tab === 'chips' && !isTw ? 'risk' : tab;

  function openTab(id: HealthTab) {
    setTab(id);
    setExpanded(true);
  }

  // === 摘要 chips 數值（缺值顯「—」，仍可點開看明細） ===
  const riskVal = s?.risk?.vol30d != null
    ? `${s.risk.level ? RISK_LEVEL_LABEL[s.risk.level] + '·' : ''}${Math.round(s.risk.vol30d * 100)}%`
    : '—';
  const riskTone: ChipTone = s?.risk?.level === 'high' ? 'down' : s?.risk?.level === 'low' ? 'up' : 'flat';

  // 用 nextEarningsDate 「即時」算距今幾天（前端自己的時間概念）；不直接信任後端
  // daysToEarnings — 那是後端抓取/快取當下算的，跨日或 facts 快取會讓相對天數 stale。
  // 雙向：未來財報倒數「N 天後」/ 當天「今天」/ 財報剛過往上算「N 天前」。
  // 不把過去藏成「—」（否則過去 1 天 / 過去 30 天看起來一樣、也看不出財報剛公布幾天）；只有真的沒財報日才「—」。
  const earnDays = daysUntil(s?.earnings?.nextEarningsDate) ?? s?.earnings?.daysToEarnings ?? null;
  const earnVal =
    earnDays == null ? '—'
      : earnDays > 0 ? `${earnDays} 天後`
        : earnDays === 0 ? '今天'
          : `${-earnDays} 天前`;
  // 財報前後窗（前 7 天 ~ 後 3 天）都是高關注期，標 accent。
  const earnTone: ChipTone = earnDays != null && earnDays >= -3 && earnDays <= 7 ? 'accent' : 'flat';

  const pe = s?.fundamentals?.trailingPE;
  const peVal = pe != null ? pe.toFixed(1) : '—';

  const f5 = s?.chips?.foreign5d;
  const chipsVal = f5 != null ? `${f5 > 0 ? '+' : ''}${f5.toLocaleString()} 張` : '—';
  const chipsTone: ChipTone = f5 != null ? (f5 > 0 ? 'up' : f5 < 0 ? 'down' : 'flat') : 'flat';

  return (
    <div>
      {/* 摘要 chips 列：常駐，一眼看完四面向重點；點任一 chip 展開該面向明細 */}
      <div className="flex flex-wrap items-center gap-1.5">
        {summaryQ.isLoading ? (
          [0, 1, 2, ...(isTw ? [3] : [])].map((i) => (
            <div key={i} className="h-6.5 w-20 animate-pulse rounded bg-(--bg-base)" />
          ))
        ) : (
          <>
            <SummaryChip label="風險" value={riskVal} tone={riskTone} onClick={() => openTab('risk')} />
            <SummaryChip label="財報" value={earnVal} tone={earnTone} onClick={() => openTab('earnings')} />
            <SummaryChip label="本益比" value={peVal} tone="flat" onClick={() => openTab('fundamentals')} />
            {isTw && <SummaryChip label="外資5日" value={chipsVal} tone={chipsTone} onClick={() => openTab('chips')} />}
          </>
        )}
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="ml-auto inline-flex items-center gap-0.5 rounded border border-(--border) px-2 py-1 text-[11px] font-semibold text-(--text-secondary) hover:border-(--accent)"
        >
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          {expanded ? '收合' : '展開明細'}
        </button>
      </div>

      {aiRisk && ((aiRisk.failureConditions?.length ?? 0) > 0 || (aiRisk.riskWarnings?.length ?? 0) > 0) && (
        <div className="mt-2 rounded border border-(--down)/30 bg-(--down)/5 px-2.5 py-1.5">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-(--down)">⏹ AI 風險 / 破壞點</div>
          {aiRisk.failureConditions && aiRisk.failureConditions.length > 0 && (
            <ul className="space-y-0.5 text-[11px] text-(--text-secondary)">
              {aiRisk.failureConditions.map((c, i) => <li key={`f${i}`}>• {c}</li>)}
            </ul>
          )}
          {aiRisk.riskWarnings && aiRisk.riskWarnings.length > 0 && (
            <ul className="mt-1 space-y-0.5 text-[11px] text-(--text-muted)">
              {aiRisk.riskWarnings.map((w, i) => <li key={`w${i}`}>⚠ {w}</li>)}
            </ul>
          )}
        </div>
      )}

      {/* 明細：預設展開，可收合。 */}
      {expanded && (
        <div className="mt-3">
          <div className="mb-2 flex flex-wrap items-center gap-1">
            {tabs.map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] font-semibold ${
                  activeTab === id
                    ? 'border-(--accent) bg-(--accent)/15 text-(--accent)'
                    : 'border-(--border) text-(--text-secondary) hover:border-(--accent)'
                }`}
              >
                <Icon size={12} />
                {label}
              </button>
            ))}
          </div>

          {activeTab === 'risk' && <RiskDashboardPanel symbol={symbol} />}
          {activeTab === 'earnings' && <EarningsCockpit symbol={symbol} />}
          {activeTab === 'fundamentals' && <FundamentalsCard symbol={symbol} />}
          {activeTab === 'chips' && isTw && (
            <div className="grid gap-3 md:grid-cols-2">
              <InstitutionalCard symbol={symbol} />
              <MarginCard symbol={symbol} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryChip({ label, value, tone, onClick }: { label: string; value: string; tone: ChipTone; onClick: () => void }) {
  const toneCls: Record<ChipTone, string> = {
    up: 'border-(--up)/50 text-(--up)',
    down: 'border-(--down)/50 text-(--down)',
    accent: 'border-(--accent)/60 text-(--accent)',
    flat: 'border-(--border) text-(--text-secondary)',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      title="點開看明細"
      className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-[11px] font-semibold transition-colors hover:border-(--accent) ${toneCls[tone]}`}
    >
      <span className="opacity-70">{label}</span>
      <span className="tnum">{value}</span>
    </button>
  );
}
